import Dexie from 'dexie'
import type {
  OfflineCommandType,
  OfflineOrder,
  OfflinePosCommand,
  OfflineSyncIssue,
  OfflineSyncResultCategory,
} from './contracts'
import type { BigSaleDB } from './db'

export interface EnqueueOfflineCommandInput {
  contextKey: string
  deviceId: string
  previousCommandId?: string | null
  supersedesCommandId?: string | null
  aggregateLocalId: string
  aggregateServerId: number | null
  aggregateType?: 'table_session' | 'booking'
  type: OfflineCommandType
  payload: Record<string, unknown>
  occurredAt: string
  deviceWallTime: string
  deviceTimezone: string
  serverTimeOffsetMs: number
  actorUserId: number
  baseDataVersion: number | null
  bootstrapId: string
  pricingContractVersion: 1
}

export interface OfflineCommandFailure {
  category: OfflineSyncResultCategory
  code: string
  message: string | null
  payload?: Record<string, unknown>
}

export interface OfflineSavePlayerCorrection {
  client_id: number
  name: string
  phone?: string | null
  customer_id?: number | null
}

export interface OfflineSaveItemCorrection {
  local_id: string
  price_list_id?: number | null
  quantity: number
  unit_price: number
  discount_type: 'amount' | 'percent'
  discount_value: number
  note: string | null
  player_client_id: number | null
}

export interface OfflineSaveOrderCorrection {
  players?: OfflineSavePlayerCorrection[]
  note?: string | null
  discount_type?: 'amount' | 'percent'
  discount_value?: number
  items?: OfflineSaveItemCorrection[]
}

export interface ListReadyOptions {
  /**
   * A normal manual-sync entry recovers delivery attempts left in `syncing` by an interrupted
   * lifecycle. Status/diagnostic views running during an active upload must opt out explicitly.
   */
  recoverInterrupted?: boolean
  /** Commands already attempted during the current manual sync must not unlock descendants. */
  excludeCommandIds?: ReadonlySet<string>
  /** Explicit recovery flows may replay terminal commands with their immutable envelope. */
  includeTerminal?: boolean
}

export interface MarkSyncingOptions {
  /** Allow an explicit recovery flow to replay a command with an unresolved terminal issue. */
  allowTerminal?: boolean
}

interface OfflineSyncQueueDependencies {
  randomUUID?: () => string
  now?: () => string
}

const IMMUTABLE_COMMAND_TYPES: ReadonlySet<OfflineCommandType> = new Set([
  'ADJUST_TABLE_SESSION_START_TIME',
  'CHECKOUT_TABLE_SESSION',
  'CANCEL_TABLE_SESSION',
  'TRANSFER_TABLE_SESSION',
  'CHECK_IN_BOOKING',
  'CANCEL_BOOKING',
])

const CORRECTION_KEYS = new Set(['players', 'note', 'discount_type', 'discount_value', 'items'])
const PLAYER_CORRECTION_KEYS = new Set(['client_id', 'name', 'phone', 'customer_id'])
const ITEM_CORRECTION_KEYS = new Set([
  'local_id',
  'price_list_id',
  'quantity',
  'unit_price',
  'discount_type',
  'discount_value',
  'note',
  'player_client_id',
])

function clonePayload(payload: Record<string, unknown>): Record<string, unknown> {
  return structuredClone(payload)
}

function owns(record: Record<string, unknown>, key: PropertyKey): boolean {
  return Object.prototype.hasOwnProperty.call(record, key)
}

function expectedAggregateType(type: OfflineCommandType): 'table_session' | 'booking' {
  return type.endsWith('_BOOKING') ? 'booking' : 'table_session'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasOnlyKeys(record: Record<string, unknown>, allowed: ReadonlySet<string>): boolean {
  return Object.keys(record).every((key) => allowed.has(key))
}

function invalidCorrection(): never {
  throw new Error('OFFLINE_SAVE_CORRECTION_INVALID')
}

function isDiscount(type: unknown, value: unknown): type is 'amount' | 'percent' {
  return (
    (type === 'amount' || type === 'percent') &&
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= 0 &&
    (type !== 'percent' || value <= 100)
  )
}

function discountAmount(amount: number, type: 'amount' | 'percent', value: number): number {
  return Math.min(amount, type === 'percent' ? Math.round((amount * value) / 100) : value)
}

function normalizeSaveCorrection(
  order: OfflineOrder,
  input: OfflineSaveOrderCorrection,
  updatedAt: string,
): OfflineOrder {
  if (!isRecord(input) || !hasOnlyKeys(input, CORRECTION_KEYS)) invalidCorrection()
  if (owns(input, 'note') && input.note !== null && typeof input.note !== 'string') {
    invalidCorrection()
  }

  const discountType = input.discount_type ?? order.discount_type
  const discountValue = input.discount_value ?? order.discount_value
  if (!isDiscount(discountType, discountValue)) invalidCorrection()

  const playerInputs =
    input.players ??
    order.players.map((player) => ({
      client_id: player.client_id,
      name: player.name,
      phone: player.phone,
      customer_id: player.customer_id,
    }))
  if (!Array.isArray(playerInputs)) invalidCorrection()
  const playerIds = new Set<number>()
  const players = playerInputs.map((player, index) => {
    if (!isRecord(player) || !hasOnlyKeys(player, PLAYER_CORRECTION_KEYS)) invalidCorrection()
    if (
      !Number.isInteger(player.client_id) ||
      (player.client_id as number) <= 0 ||
      playerIds.has(player.client_id as number) ||
      typeof player.name !== 'string' ||
      player.name.trim() === '' ||
      (player.phone !== undefined && player.phone !== null && typeof player.phone !== 'string') ||
      (player.customer_id !== undefined &&
        player.customer_id !== null &&
        (!Number.isInteger(player.customer_id) || (player.customer_id as number) <= 0))
    ) {
      invalidCorrection()
    }
    playerIds.add(player.client_id as number)

    return {
      client_id: player.client_id as number,
      name: player.name,
      phone: (player.phone as string | null | undefined) ?? null,
      customer_id: (player.customer_id as number | null | undefined) ?? null,
      sort_order: index,
    }
  })

  const itemInputs =
    input.items ??
    order.items.map((item) => ({
      local_id: item.local_id,
      price_list_id: item.price_list_id,
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount_type: item.discount_type,
      discount_value: item.discount_value,
      note: item.note,
      player_client_id: item.player_client_id,
    }))
  if (!Array.isArray(itemInputs)) invalidCorrection()
  const itemIds = new Set<string>()
  const sourceItems = new Map(order.items.map((item) => [item.local_id, item]))
  const items = itemInputs.map((item, index) => {
    if (!isRecord(item) || !hasOnlyKeys(item, ITEM_CORRECTION_KEYS)) invalidCorrection()
    const source = typeof item.local_id === 'string' ? sourceItems.get(item.local_id) : undefined
    if (
      !source ||
      itemIds.has(source.local_id) ||
      typeof item.quantity !== 'number' ||
      !Number.isFinite(item.quantity) ||
      item.quantity <= 0 ||
      typeof item.unit_price !== 'number' ||
      !Number.isFinite(item.unit_price) ||
      item.unit_price < 0 ||
      (item.price_list_id !== null &&
        item.price_list_id !== undefined &&
        (!Number.isInteger(item.price_list_id) || item.price_list_id < 1)) ||
      !isDiscount(item.discount_type, item.discount_value) ||
      (item.note !== null && typeof item.note !== 'string') ||
      (item.player_client_id !== null &&
        (!Number.isInteger(item.player_client_id) ||
          !playerIds.has(item.player_client_id as number)))
    ) {
      invalidCorrection()
    }
    itemIds.add(source.local_id)
    const gross = Math.round(item.quantity * item.unit_price)
    const lineDiscount = discountAmount(gross, item.discount_type, item.discount_value)

    return {
      ...source,
      quantity: item.quantity,
      price_list_id: (item.price_list_id as number | null | undefined) ?? source.price_list_id ?? null,
      unit_price: item.unit_price,
      discount_type: item.discount_type,
      discount_value: item.discount_value,
      discount_amount: lineDiscount,
      vat_amount: 0,
      line_total: gross - lineDiscount,
      note: item.note,
      player_client_id: item.player_client_id,
      sort_order: index,
    }
  })
  const subtotal = items.reduce((sum, item) => sum + item.line_total, 0)
  const orderDiscount = discountAmount(subtotal, discountType, discountValue)

  return {
    ...order,
    players,
    note: owns(input, 'note') ? (input.note ?? null) : order.note,
    discount_type: discountType,
    discount_value: discountValue,
    discount_amount: orderDiscount,
    subtotal,
    total_amount: subtotal - orderDiscount,
    items,
    updated_at: updatedAt,
  }
}

function authoritativeOrderPayload(order: OfflineOrder): Record<string, unknown> {
  return {
    session_local_id: order.session_local_id,
    session_server_id: order.session_server_id,
    order_server_id: order.order_server_id,
    data_version: order.data_version,
    players: structuredClone(order.players),
    note: order.note,
    table_discount_type: order.table_discount_type ?? 'amount',
    table_discount_value: order.table_discount_value ?? 0,
    discount_type: order.discount_type,
    discount_value: order.discount_value,
    voucher_codes: structuredClone(order.voucher_codes ?? []),
    voucher_discount_amount: order.voucher_discount_amount ?? 0,
    discount_amount: order.discount_amount,
    subtotal: order.subtotal,
    total_amount: order.total_amount,
    items: structuredClone(order.items),
    updated_at: order.updated_at,
  }
}

export class OfflineSyncQueue {
  private readonly randomUUID: () => string
  private readonly currentTime: () => string

  constructor(
    private readonly database: BigSaleDB,
    dependencies: OfflineSyncQueueDependencies = {},
  ) {
    this.randomUUID = dependencies.randomUUID ?? (() => crypto.randomUUID())
    this.currentTime = dependencies.now ?? (() => new Date().toISOString())
  }

  async enqueue(input: EnqueueOfflineCommandInput): Promise<OfflinePosCommand> {
    return this.database.transaction(
      'rw',
      [this.database.offlineContexts, this.database.outboxCommands],
      async () => this.enqueueInCurrentTransaction(input),
    )
  }

  async enqueueInTransaction(input: EnqueueOfflineCommandInput): Promise<OfflinePosCommand> {
    if (!Dexie.currentTransaction) {
      throw new Error('OFFLINE_COMMAND_TRANSACTION_REQUIRED')
    }

    return this.enqueueInCurrentTransaction(input)
  }

  async compactPendingSave(input: EnqueueOfflineCommandInput): Promise<OfflinePosCommand> {
    if (input.type !== 'SAVE_TABLE_ORDER') {
      throw new Error('OFFLINE_SAVE_COMMAND_REQUIRED')
    }

    return this.database.transaction(
      'rw',
      [this.database.offlineContexts, this.database.outboxCommands],
      async () => this.enqueueInCurrentTransaction(input),
    )
  }

  async all(contextKey?: string): Promise<OfflinePosCommand[]> {
    const commands = contextKey
      ? await this.contextCommands(contextKey)
      : await this.database.outboxCommands.toArray()

    return commands.sort((left, right) => {
      const byContext = left.context_key.localeCompare(right.context_key)
      return byContext === 0 ? left.sequence - right.sequence : byContext
    })
  }

  get(commandId: string): Promise<OfflinePosCommand | undefined> {
    return this.database.outboxCommands.get(commandId)
  }

  async replaceFailedEditableSave(
    contextKey: string,
    commandId: string,
    correction: OfflineSaveOrderCorrection,
  ): Promise<OfflinePosCommand> {
    return this.database.transaction(
      'rw',
      [this.database.outboxCommands, this.database.orders, this.database.syncIssues],
      async () => {
        const failed = await this.requireCommand(contextKey, commandId)
        if (failed.type !== 'SAVE_TABLE_ORDER' || failed.status !== 'failed') {
          throw new Error('OFFLINE_EDITABLE_FAILED_SAVE_REQUIRED')
        }

        const unresolvedIssues = await this.database.syncIssues
          .where('command_id')
          .equals(commandId)
          .filter((issue) => issue.resolved_at === null)
          .toArray()
        if (
          !unresolvedIssues.some((issue) => issue.category === 'validation') ||
          unresolvedIssues.some((issue) => issue.category === 'integrity')
        ) {
          throw new Error('OFFLINE_VALIDATION_FAILED_SAVE_REQUIRED')
        }

        const commands = await this.contextCommands(failed.context_key)
        const liveDependents = this.dependentCommands(failed.command_id, commands).filter(
          (command) => command.status !== 'superseded',
        )
        if (liveDependents.some((command) => IMMUTABLE_COMMAND_TYPES.has(command.type))) {
          throw new Error('OFFLINE_SAVE_HAS_IMMUTABLE_DEPENDENT')
        }
        if (
          liveDependents.some(
            (command) => command.type !== 'SAVE_TABLE_ORDER' || command.status === 'acknowledged',
          )
        ) {
          throw new Error('OFFLINE_SAVE_HAS_FINAL_DEPENDENT')
        }
        if (liveDependents.some((command) => command.status === 'syncing')) {
          throw new Error('OFFLINE_SAVE_DEPENDENT_SYNCING')
        }

        const order = await this.database.orders.get([
          failed.context_key,
          failed.aggregate_local_id,
        ])
        if (!order) throw new Error('OFFLINE_ORDER_MISSING')

        const deviceWallTime = this.currentTime()
        const occurredAt = new Date(
          Date.parse(deviceWallTime) + failed.server_time_offset_ms,
        ).toISOString()
        const updatedOrder = normalizeSaveCorrection(order, correction, occurredAt)
        const sequence = await this.nextSequence(failed.context_key)
        const replacement: OfflinePosCommand = {
          ...failed,
          command_id: this.randomUUID(),
          sequence,
          previous_command_id: failed.previous_command_id,
          supersedes_command_id: failed.command_id,
          payload: {
            ...clonePayload(failed.payload),
            ...authoritativeOrderPayload(updatedOrder),
            ...(failed.base_data_version === null ? { data_version: 0 } : {}),
          },
          occurred_at: occurredAt,
          device_wall_time: deviceWallTime,
          status: 'pending',
          attempts: 0,
          error_code: null,
          error_message: null,
        }

        await this.database.orders.put(updatedOrder)

        const supersededIds = [
          failed.command_id,
          ...liveDependents.map((command) => command.command_id),
        ]
        await Promise.all(
          supersededIds.map((supersededId) =>
            this.database.outboxCommands.update(supersededId, { status: 'superseded' }),
          ),
        )
        await this.database.outboxCommands.add(replacement)
        const issuesToResolve = await this.database.syncIssues
          .where('context_key')
          .equals(failed.context_key)
          .filter(
            (issue) =>
              issue.resolved_at === null &&
              issue.command_id !== null &&
              supersededIds.includes(issue.command_id),
          )
          .toArray()
        await Promise.all(
          issuesToResolve.map((issue) =>
            this.database.syncIssues.update(issue.id!, { resolved_at: replacement.occurred_at }),
          ),
        )

        return replacement
      },
    )
  }

  async discardClosedSessionConflict(
    contextKey: string,
    commandId: string,
  ): Promise<OfflinePosCommand[]> {
    return this.database.transaction(
      'rw',
      [this.database.outboxCommands, this.database.syncIssues],
      async () => {
        const failed = await this.requireCommand(contextKey, commandId)
        if (failed.status !== 'failed') {
          throw new Error('OFFLINE_FAILED_COMMAND_REQUIRED')
        }

        const unresolvedIssues = await this.database.syncIssues
          .where('command_id')
          .equals(commandId)
          .filter((issue) => issue.resolved_at === null)
          .toArray()
        const hasClosedSessionConflict = unresolvedIssues.some((issue) => {
          if (issue.category !== 'validation') return false
          const errors = issue.payload.errors
          return isRecord(errors) && Array.isArray(errors.status) && errors.status.length > 0
        })
        if (!hasClosedSessionConflict) {
          throw new Error('OFFLINE_CLOSED_SESSION_CONFLICT_REQUIRED')
        }

        const commands = await this.contextCommands(contextKey)
        const affected = [failed, ...this.dependentCommands(commandId, commands)].filter(
          (command) => command.status !== 'superseded',
        )
        if (affected.some((command) => command.status === 'acknowledged')) {
          throw new Error('OFFLINE_COMMAND_ALREADY_ACKNOWLEDGED')
        }
        if (affected.some((command) => command.status === 'syncing')) {
          throw new Error('OFFLINE_COMMAND_SYNCING')
        }

        const discardedAt = this.currentTime()
        const discarded = affected.map(
          (command): OfflinePosCommand => ({
            ...command,
            status: 'superseded',
            error_code: null,
            error_message: null,
          }),
        )
        await this.database.outboxCommands.bulkPut(discarded)

        const discardedIds = new Set(discarded.map((command) => command.command_id))
        const issuesToResolve = await this.database.syncIssues
          .where('context_key')
          .equals(contextKey)
          .filter(
            (issue) =>
              issue.resolved_at === null &&
              issue.command_id !== null &&
              discardedIds.has(issue.command_id),
          )
          .toArray()
        await Promise.all(
          issuesToResolve.map((issue) =>
            this.database.syncIssues.update(issue.id!, { resolved_at: discardedAt }),
          ),
        )

        return discarded
      },
    )
  }

  async recoverInterruptedSync(contextKey: string): Promise<OfflinePosCommand[]> {
    return this.database.transaction(
      'rw',
      [this.database.offlineContexts, this.database.outboxCommands, this.database.syncIssues],
      async () => {
        await this.requireContext(contextKey)
        const interrupted = (await this.contextCommands(contextKey)).filter(
          (command) => command.status === 'syncing',
        )
        if (interrupted.length === 0) return []

        const recovered = interrupted.map(
          (command): OfflinePosCommand => ({
            ...command,
            status: 'failed',
            error_code: 'offline_pos.sync_interrupted',
            error_message: 'The previous offline synchronization was interrupted.',
          }),
        )
        await this.database.outboxCommands.bulkPut(recovered)
        await this.database.syncIssues.bulkAdd(
          recovered.map((command) => ({
            context_key: command.context_key,
            command_id: command.command_id,
            category: 'retryable' as const,
            code: 'offline_pos.sync_interrupted',
            message: command.error_message,
            payload: { attempts: command.attempts },
            created_at: this.currentTime(),
            resolved_at: null,
          })),
        )
        return recovered
      },
    )
  }

  async listReady(
    contextKey: string,
    options: ListReadyOptions = {},
  ): Promise<OfflinePosCommand[]> {
    if (options.recoverInterrupted !== false) {
      await this.recoverInterruptedSync(contextKey)
    } else {
      await this.requireContext(contextKey)
    }
    const commands = await this.contextCommands(contextKey)
    const acknowledged = new Set(
      commands
        .filter((command) => command.status === 'acknowledged')
        .map((command) => command.command_id),
    )
    const terminalCommandIds = await this.terminalCommandIds(contextKey)
    const readyIds = new Set<string>()
    const ready: OfflinePosCommand[] = []

    for (const command of commands) {
      if (
        options.excludeCommandIds?.has(command.command_id) ||
        command.status === 'acknowledged' ||
        command.status === 'superseded' ||
        command.status === 'syncing' ||
        (!options.includeTerminal && terminalCommandIds.has(command.command_id))
      ) {
        continue
      }

      const previous = command.previous_command_id
      if (previous !== null && !acknowledged.has(previous) && !readyIds.has(previous)) {
        continue
      }

      if (!['pending', 'failed', 'blocked'].includes(command.status)) {
        continue
      }

      ready.push(command)
      readyIds.add(command.command_id)
    }

    return ready
  }

  async markSyncing(
    contextKey: string,
    commandIds: string[],
    options: MarkSyncingOptions = {},
  ): Promise<OfflinePosCommand[]> {
    const uniqueIds = Array.from(new Set(commandIds))
    if (uniqueIds.length === 0) {
      await this.requireContext(contextKey)
      return []
    }

    return this.database.transaction(
      'rw',
      [this.database.outboxCommands, this.database.syncIssues],
      async () => {
        const commands = await this.database.outboxCommands.bulkGet(uniqueIds)
        if (commands.some((command) => command === undefined)) {
          throw new Error('OFFLINE_COMMAND_MISSING')
        }

        const present = commands as OfflinePosCommand[]
        if (present.some((command) => command.context_key !== contextKey)) {
          throw new Error('OFFLINE_COMMAND_CONTEXT_MISMATCH')
        }
        if (
          present.some(
            (command) =>
              command.status === 'acknowledged' ||
              command.status === 'superseded' ||
              command.status === 'syncing',
          )
        ) {
          throw new Error('OFFLINE_COMMAND_NOT_READY')
        }

        const terminalCommandIds = await this.terminalCommandIds(contextKey)
        if (
          !options.allowTerminal &&
          present.some((command) => terminalCommandIds.has(command.command_id))
        ) {
          throw new Error('OFFLINE_COMMAND_TERMINAL')
        }

        const selectedIds = new Set(uniqueIds)
        const contextCommands = await this.contextCommands(contextKey)
        const byId = new Map(contextCommands.map((command) => [command.command_id, command]))
        if (
          present.some((command) => {
            if (command.previous_command_id === null) return false
            const previous = byId.get(command.previous_command_id)
            return (
              !previous ||
              (previous.status !== 'acknowledged' &&
                (!selectedIds.has(previous.command_id) || previous.sequence >= command.sequence))
            )
          })
        ) {
          throw new Error('OFFLINE_COMMAND_DEPENDENCY_NOT_READY')
        }

        const updated = present.map((command) => ({
          ...command,
          status: 'syncing' as const,
          attempts: command.attempts + 1,
        }))
        await this.database.outboxCommands.bulkPut(updated)
        return updated
      },
    )
  }

  async countUnacknowledged(contextKey: string): Promise<number> {
    await this.requireContext(contextKey)
    return this.database.outboxCommands
      .where('[context_key+status]')
      .anyOf(
        (['pending', 'syncing', 'failed', 'blocked'] as const).map((status) => [
          contextKey,
          status,
        ]),
      )
      .count()
  }

  async markRetryable(
    contextKey: string,
    commandIds: string[],
    failure: Omit<OfflineCommandFailure, 'category'>,
  ): Promise<OfflinePosCommand[]> {
    return this.markDeliveryFailure(contextKey, commandIds, {
      ...failure,
      category: 'retryable',
    })
  }

  async markDeliveryFailure(
    contextKey: string,
    commandIds: string[],
    failure: OfflineCommandFailure,
  ): Promise<OfflinePosCommand[]> {
    const uniqueIds = Array.from(new Set(commandIds))
    if (uniqueIds.length === 0) return []

    return this.database.transaction(
      'rw',
      [this.database.outboxCommands, this.database.syncIssues],
      async () => {
        const commands = await this.database.outboxCommands.bulkGet(uniqueIds)
        if (commands.some((command) => command === undefined)) {
          throw new Error('OFFLINE_COMMAND_MISSING')
        }
        const present = commands as OfflinePosCommand[]
        if (present.some((command) => command.context_key !== contextKey)) {
          throw new Error('OFFLINE_COMMAND_CONTEXT_MISMATCH')
        }
        if (present.some((command) => command.status !== 'syncing')) {
          throw new Error('OFFLINE_COMMAND_NOT_SYNCING')
        }

        const failed = present.map(
          (command): OfflinePosCommand => ({
            ...command,
            status: 'failed',
            error_code: failure.code,
            error_message: failure.message,
          }),
        )
        await this.database.outboxCommands.bulkPut(failed)
        await this.database.syncIssues.bulkAdd(
          failed.map((command) => ({
            context_key: contextKey,
            command_id: command.command_id,
            category: failure.category,
            code: failure.code,
            message: failure.message,
            payload: structuredClone(failure.payload ?? {}),
            created_at: this.currentTime(),
            resolved_at: null,
          })),
        )
        return failed
      },
    )
  }

  async retryTransientCommand(contextKey: string, commandId: string): Promise<OfflinePosCommand> {
    return this.database.transaction(
      'rw',
      [this.database.outboxCommands, this.database.syncIssues],
      async () => {
        const command = await this.requireCommand(contextKey, commandId)
        if (command.status !== 'failed') throw new Error('OFFLINE_RETRYABLE_COMMAND_REQUIRED')
        const retryableIssues = await this.database.syncIssues
          .where('command_id')
          .equals(commandId)
          .filter((issue) => issue.resolved_at === null && issue.category === 'retryable')
          .toArray()
        if (retryableIssues.length === 0) throw new Error('OFFLINE_RETRYABLE_COMMAND_REQUIRED')

        const pending: OfflinePosCommand = {
          ...command,
          status: 'pending',
          error_code: null,
          error_message: null,
        }
        await this.database.outboxCommands.put(pending)
        const resolvedAt = this.currentTime()
        await Promise.all(
          retryableIssues.map((issue) =>
            this.database.syncIssues.update(issue.id!, { resolved_at: resolvedAt }),
          ),
        )
        return pending
      },
    )
  }

  async markReauthenticated(contextKey: string, commandId: string): Promise<OfflinePosCommand> {
    return this.database.transaction(
      'rw',
      [this.database.outboxCommands, this.database.syncIssues],
      async () => {
        const command = await this.requireCommand(contextKey, commandId)
        if (command.status !== 'failed') throw new Error('OFFLINE_AUTH_COMMAND_REQUIRED')
        const authIssues = await this.database.syncIssues
          .where('command_id')
          .equals(commandId)
          .filter((issue) => issue.resolved_at === null && issue.category === 'auth')
          .toArray()
        if (authIssues.length === 0) throw new Error('OFFLINE_AUTH_COMMAND_REQUIRED')

        const pending: OfflinePosCommand = {
          ...command,
          status: 'pending',
          error_code: null,
          error_message: null,
        }
        await this.database.outboxCommands.put(pending)
        const resolvedAt = this.currentTime()
        await Promise.all(
          authIssues.map((issue) =>
            this.database.syncIssues.update(issue.id!, { resolved_at: resolvedAt }),
          ),
        )
        return pending
      },
    )
  }

  async acknowledge(contextKey: string, commandId: string): Promise<OfflinePosCommand> {
    return this.database.transaction(
      'rw',
      [this.database.outboxCommands, this.database.syncIssues],
      async () => {
        const command = await this.requireCommand(contextKey, commandId)
        if (command.status === 'acknowledged') return command
        if (command.status !== 'syncing') throw new Error('OFFLINE_COMMAND_NOT_SYNCING')

        const acknowledged: OfflinePosCommand = {
          ...command,
          status: 'acknowledged',
          error_code: null,
          error_message: null,
        }
        await this.database.outboxCommands.put(acknowledged)
        await this.resolveIssues(commandId)
        return acknowledged
      },
    )
  }

  async fail(
    contextKey: string,
    commandId: string,
    failure: OfflineCommandFailure,
  ): Promise<OfflinePosCommand> {
    return this.database.transaction(
      'rw',
      [this.database.outboxCommands, this.database.syncIssues],
      async () => {
        const command = await this.requireCommand(contextKey, commandId)
        if (command.status !== 'syncing') throw new Error('OFFLINE_COMMAND_NOT_SYNCING')

        const failed: OfflinePosCommand = {
          ...command,
          status: 'failed',
          error_code: failure.code,
          error_message: failure.message,
        }
        await this.database.outboxCommands.put(failed)
        await this.database.syncIssues.add({
          context_key: command.context_key,
          command_id: command.command_id,
          category: failure.category,
          code: failure.code,
          message: failure.message,
          payload: structuredClone(failure.payload ?? {}),
          created_at: this.currentTime(),
          resolved_at: null,
        })
        return failed
      },
    )
  }

  async blockDependents(contextKey: string, commandId: string): Promise<OfflinePosCommand[]> {
    return this.database.transaction(
      'rw',
      [this.database.outboxCommands, this.database.syncIssues],
      async () => {
        const failed = await this.requireCommand(contextKey, commandId)
        if (failed.status !== 'failed') throw new Error('OFFLINE_FAILED_COMMAND_REQUIRED')
        const commands = await this.contextCommands(failed.context_key)
        const dependentIds = new Set([failed.command_id])
        const blocked: OfflinePosCommand[] = []

        for (const command of commands) {
          if (
            command.previous_command_id === null ||
            !dependentIds.has(command.previous_command_id) ||
            command.status === 'acknowledged' ||
            command.status === 'superseded'
          ) {
            continue
          }

          dependentIds.add(command.command_id)
          const updated: OfflinePosCommand = {
            ...command,
            status: 'blocked',
            error_code: 'offline_pos.dependency_blocked',
            error_message: `Blocked by ${failed.command_id}`,
          }
          await this.database.outboxCommands.put(updated)
          await this.database.syncIssues.add({
            context_key: command.context_key,
            command_id: command.command_id,
            category: 'dependency',
            code: 'offline_pos.dependency_blocked',
            message: updated.error_message,
            payload: { blocked_by_command_id: failed.command_id },
            created_at: this.currentTime(),
            resolved_at: null,
          })
          blocked.push(updated)
        }

        return blocked
      },
    )
  }

  private async enqueueInCurrentTransaction(
    input: EnqueueOfflineCommandInput,
  ): Promise<OfflinePosCommand> {
    await this.validateContext(input)
    const aggregateType = input.aggregateType ?? 'table_session'
    if (aggregateType !== expectedAggregateType(input.type)) {
      throw new Error('OFFLINE_COMMAND_AGGREGATE_TYPE_MISMATCH')
    }

    const commands = await this.contextCommands(input.contextKey)
    const aggregateCommands = commands.filter(
      (command) =>
        command.aggregate_local_id === input.aggregateLocalId && command.status !== 'superseded',
    )
    const latestAggregateCommand = aggregateCommands.at(-1)

    if (
      input.type === 'SAVE_TABLE_ORDER' &&
      latestAggregateCommand?.type === 'SAVE_TABLE_ORDER' &&
      latestAggregateCommand.status === 'pending' &&
      latestAggregateCommand.attempts === 0 &&
      !this.hasImmutableDependent(latestAggregateCommand.command_id, commands)
    ) {
      const compacted: OfflinePosCommand = {
        ...latestAggregateCommand,
        payload: clonePayload(input.payload),
        occurred_at: input.occurredAt,
        device_wall_time: input.deviceWallTime,
        device_timezone: input.deviceTimezone,
        server_time_offset_ms: input.serverTimeOffsetMs,
        actor_user_id: input.actorUserId,
      }
      await this.database.outboxCommands.put(compacted)
      return compacted
    }

    const previousCommandId = owns(input as unknown as Record<string, unknown>, 'previousCommandId')
      ? (input.previousCommandId ?? null)
      : (latestAggregateCommand?.command_id ?? null)
    if (previousCommandId !== null) {
      const previous = commands.find((command) => command.command_id === previousCommandId)
      const transferDependency =
        (previous?.type === 'TRANSFER_TABLE_SESSION' &&
          previous.payload.target_session_local_id === input.aggregateLocalId) ||
        (previous?.type === 'CHECK_IN_BOOKING' &&
          Array.isArray(previous.payload.session_local_ids) &&
          previous.payload.session_local_ids.includes(input.aggregateLocalId))
      const bookingCheckInDependency =
        input.type === 'CHECK_IN_BOOKING' &&
        previous?.aggregate_type === 'booking' &&
        previous.aggregate_local_id === input.payload.booking_local_id
      const crossAggregateDependency =
        transferDependency &&
        (previous?.type === 'CHECK_IN_BOOKING' || previous?.aggregate_type === aggregateType)
      if (
        !previous ||
        (previous.aggregate_type !== aggregateType && !crossAggregateDependency) ||
        (previous.aggregate_local_id !== input.aggregateLocalId &&
          !transferDependency &&
          !bookingCheckInDependency) ||
        previous.status === 'superseded'
      ) {
        throw new Error('OFFLINE_COMMAND_DEPENDENCY_MISMATCH')
      }
    }
    const command: OfflinePosCommand = {
      command_id: this.randomUUID(),
      context_key: input.contextKey,
      device_id: input.deviceId,
      sequence: await this.nextSequence(input.contextKey),
      previous_command_id: previousCommandId,
      supersedes_command_id: input.supersedesCommandId ?? null,
      aggregate_type: aggregateType,
      aggregate_local_id: input.aggregateLocalId,
      aggregate_server_id: input.aggregateServerId,
      type: input.type,
      payload_version: 1,
      payload: clonePayload(input.payload),
      occurred_at: input.occurredAt,
      device_wall_time: input.deviceWallTime,
      device_timezone: input.deviceTimezone,
      server_time_offset_ms: input.serverTimeOffsetMs,
      actor_user_id: input.actorUserId,
      base_data_version: input.baseDataVersion,
      bootstrap_id: input.bootstrapId,
      pricing_contract_version: input.pricingContractVersion,
      status: 'pending',
      attempts: 0,
      error_code: null,
      error_message: null,
    }
    await this.database.outboxCommands.add(command)
    return command
  }

  private async validateContext(input: EnqueueOfflineCommandInput): Promise<void> {
    const context = await this.database.offlineContexts.get(input.contextKey)
    if (!context) throw new Error('OFFLINE_CONTEXT_MISSING')
    if (context.mode !== 'offline') throw new Error('OFFLINE_CONTEXT_NOT_WRITABLE')
    if (context.device_id !== input.deviceId) throw new Error('OFFLINE_COMMAND_DEVICE_MISMATCH')
    if (context.actor_user_id !== input.actorUserId) {
      throw new Error('OFFLINE_COMMAND_ACTOR_MISMATCH')
    }
    if (context.server_time_offset_ms !== input.serverTimeOffsetMs) {
      throw new Error('OFFLINE_COMMAND_OFFSET_MISMATCH')
    }
    if (context.bootstrap_id !== input.bootstrapId) {
      throw new Error('OFFLINE_COMMAND_BOOTSTRAP_MISMATCH')
    }
    if (context.pricing_contract_version !== input.pricingContractVersion) {
      throw new Error('OFFLINE_PRICING_CONTRACT_MISMATCH')
    }
  }

  private contextCommands(contextKey: string): Promise<OfflinePosCommand[]> {
    return this.database.outboxCommands
      .where('[context_key+sequence]')
      .between([contextKey, Dexie.minKey], [contextKey, Dexie.maxKey])
      .toArray()
  }

  private async nextSequence(contextKey: string): Promise<number> {
    const latest = await this.database.outboxCommands
      .where('[context_key+sequence]')
      .between([contextKey, Dexie.minKey], [contextKey, Dexie.maxKey])
      .last()

    return (latest?.sequence ?? 0) + 1
  }

  private hasImmutableDependent(commandId: string, commands: OfflinePosCommand[]): boolean {
    return this.dependentCommands(commandId, commands).some(
      (command) => command.status !== 'superseded' && IMMUTABLE_COMMAND_TYPES.has(command.type),
    )
  }

  private dependentCommands(commandId: string, commands: OfflinePosCommand[]): OfflinePosCommand[] {
    const dependentIds = new Set([commandId])
    const dependents: OfflinePosCommand[] = []
    for (const command of commands) {
      if (command.previous_command_id === null || !dependentIds.has(command.previous_command_id)) {
        continue
      }
      dependentIds.add(command.command_id)
      dependents.push(command)
    }

    return dependents
  }

  private async terminalCommandIds(contextKey: string): Promise<Set<string>> {
    const issues = await this.database.syncIssues
      .where('context_key')
      .equals(contextKey)
      .filter(
        (issue) =>
          issue.resolved_at === null &&
          issue.command_id !== null &&
          (issue.category === 'validation' ||
            issue.category === 'integrity' ||
            issue.category === 'auth') &&
          // A checkout chronology validation can be repaired by replaying the immutable
          // command after the server normalizes a stale offline adjustment projection.
          // Do not keep such commands permanently hidden behind the terminal-issue gate.
          !(
            issue.category === 'validation' &&
            issue.payload &&
            typeof issue.payload === 'object' &&
            'errors' in issue.payload &&
            issue.payload.errors &&
            typeof issue.payload.errors === 'object' &&
            'ended_at' in issue.payload.errors
          ),
      )
      .toArray()

    return new Set(issues.map((issue) => issue.command_id!))
  }

  private async requireContext(contextKey: string): Promise<void> {
    if (!(await this.database.offlineContexts.get(contextKey))) {
      throw new Error('OFFLINE_CONTEXT_MISSING')
    }
  }

  private async requireCommand(contextKey: string, commandId: string): Promise<OfflinePosCommand> {
    const command = await this.database.outboxCommands.get(commandId)
    if (!command) throw new Error('OFFLINE_COMMAND_MISSING')
    if (command.context_key !== contextKey) throw new Error('OFFLINE_COMMAND_CONTEXT_MISMATCH')
    return command
  }

  private async resolveIssues(commandId: string): Promise<void> {
    const issues: OfflineSyncIssue[] = await this.database.syncIssues
      .where('command_id')
      .equals(commandId)
      .filter((issue) => issue.resolved_at === null)
      .toArray()
    const resolvedAt = this.currentTime()
    await Promise.all(
      issues.map((issue) =>
        this.database.syncIssues.update(issue.id!, { resolved_at: resolvedAt }),
      ),
    )
  }
}
